import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  GitBranch, ChevronLeft, Save, RefreshCw, AlertTriangle, Zap,
  Clock, User, Calendar, Shield, FileText, HardDrive, History, Sparkles, BookOpen,
} from 'lucide-react';

function hasRole(user, role) {
  return user?.role === role || (user?.extra_roles || []).includes(role);
}
function canApprove(user) {
  return hasRole(user, 'change_manager') || hasRole(user, 'admin');
}
function canWrite(user) {
  return hasRole(user, 'technician') || hasRole(user, 'admin') || hasRole(user, 'change_manager');
}

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const TYPE_STYLE = {
  normal:    { label: 'Normal',     bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-dim)' },
  standard:  { label: 'Estándar',  bg: 'var(--green-dim)', color: 'var(--green)', border: 'var(--green-dim)' },
  emergency: { label: 'Emergencia', bg: 'var(--red-dim)', color: 'var(--red)', border: 'var(--red-dim)' },
};

const STATUS_STYLE = {
  draft:              { label: 'Borrador',          bg: 'var(--bg-subtle)', color: 'var(--ink-2)', border: 'var(--border)' },
  pending_approval:   { label: 'Pend. aprobación',  bg: 'var(--amber-dim)', color: 'var(--amber)', border: 'var(--amber-dim)' },
  approved:           { label: 'Aprobado',          bg: 'var(--green-dim)', color: 'var(--green)', border: 'var(--green-dim)' },
  implementing:       { label: 'Implementando',     bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-dim)' },
  review:             { label: 'En revisión',       bg: 'var(--bg-subtle)', color: 'var(--ink-2)', border: 'var(--border)' },
  closed:             { label: 'Cerrado',           bg: 'var(--bg-subtle)', color: 'var(--ink-2)', border: 'var(--border)' },
  rejected:           { label: 'Rechazado',         bg: 'var(--red-dim)', color: 'var(--red)', border: 'var(--red-dim)' },
  cancelled:          { label: 'Cancelado',         bg: 'var(--bg-subtle)', color: 'var(--ink-3)', border: 'var(--border)' },
};

const TRANSITIONS = {
  draft:            ['pending_approval', 'cancelled'],
  pending_approval: ['approved', 'rejected', 'draft'],
  approved:         ['implementing', 'cancelled'],
  implementing:     ['review', 'cancelled'],
  review:           ['closed', 'implementing'],
  rejected:         ['draft'],
  closed:           [],
  cancelled:        [],
};

const TRANSITION_LABELS = {
  pending_approval: 'Enviar a aprobación',
  approved:         'Aprobar',
  rejected:         'Rechazar',
  implementing:     'Iniciar implementación',
  review:           'Pasar a revisión',
  closed:           'Cerrar',
  draft:            'Devolver a borrador',
  cancelled:        'Cancelar',
};

const TRANSITION_STYLE = {
  approved:     { bg: 'var(--green)', color: 'var(--surface)' },
  implementing: { bg: 'var(--accent)', color: 'var(--surface)' },
  closed:       { bg: 'var(--ink)', color: 'var(--surface)' },
  rejected:     { bg: 'var(--red)', color: 'var(--surface)' },
  cancelled:    { bg: 'var(--ink-2)', color: 'var(--surface)' },
  pending_approval: { bg: 'var(--amber)', color: 'var(--surface)' },
  draft:        { bg: '#fff', color: 'var(--ink-2)', border: '1px solid var(--border)' },
  review:       { bg: 'var(--ink-2)', color: 'var(--surface)' },
};

function Badge({ s }) {
  const st = STATUS_STYLE[s] || STATUS_STYLE.draft;
  return (
    <span style={{ display: 'inline-block', fontSize: 12, fontWeight: 600, color: st.color,}}>
      {st.label}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, disabled, type = 'text', ...rest }) {
  return (
    <input type={type} value={value || ''} onChange={onChange} disabled={disabled}
      style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, color: 'var(--ink)', background: disabled ? 'var(--bg-subtle)' : '#fff', boxSizing: 'border-box' }}
      {...rest} />
  );
}

function Textarea({ value, onChange, disabled, rows = 4 }) {
  return (
    <textarea value={value || ''} onChange={onChange} disabled={disabled} rows={rows}
      style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, color: 'var(--ink)', background: disabled ? 'var(--bg-subtle)' : '#fff', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
  );
}

function Select({ value, onChange, disabled, children }) {
  return (
    <select value={value || ''} onChange={onChange} disabled={disabled}
      style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13, color: 'var(--ink)', background: disabled ? 'var(--bg-subtle)' : '#fff', boxSizing: 'border-box' }}>
      {children}
    </select>
  );
}

const TABS = ['Detalles', 'Plan', 'Activos afectados', 'Historial'];

// ── Página nueva (crear RFC) ─────────────────────────────────────────────────
export function ChangeNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '', description: '', change_type: 'normal',
    priority: 'media', risk_level: 'bajo', impact: 'bajo',
    implementation_plan: '', rollback_plan: '', test_plan: '',
    planned_start: '', planned_end: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const token = localStorage.getItem('token');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) { setError('El título es requerido.'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API_URL}/changes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Error creando cambio.'); return; }
      navigate(`/changes/${data.id}`);
    } catch { setError('Error de conexión.'); }
    finally { setSaving(false); }
  }

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div style={{ maxWidth: 820 }}>
      <button onClick={() => navigate('/changes')}
        style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--ink-2)', fontSize: 13, cursor: 'pointer', marginBottom: 16, padding: 0 }}>
        <ChevronLeft size={14} /> Volver
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <GitBranch size={20} style={{ opacity: 0.7 }} />
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Nuevo RFC</h1>
      </div>

      {error && <div style={{ background: 'var(--red-dim)', border: '1px solid var(--red-dim)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)', marginBottom: 16 }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ padding: 24, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <Field label="Tipo de cambio">
              <Select value={form.change_type} onChange={upd('change_type')}>
                <option value="normal">Normal</option>
                <option value="standard">Estándar (pre-aprobado)</option>
                <option value="emergency">Emergencia</option>
              </Select>
            </Field>
            <Field label="Prioridad">
              <Select value={form.priority} onChange={upd('priority')}>
                <option value="baja">Baja</option>
                <option value="media">Media</option>
                <option value="alta">Alta</option>
                <option value="critica">Crítica</option>
              </Select>
            </Field>
            <Field label="Riesgo">
              <Select value={form.risk_level} onChange={upd('risk_level')}>
                <option value="bajo">Bajo</option>
                <option value="medio">Medio</option>
                <option value="alto">Alto</option>
                <option value="critico">Crítico</option>
              </Select>
            </Field>
          </div>
          <Field label="Título *">
            <Input value={form.title} onChange={upd('title')} placeholder="Descripción breve del cambio" />
          </Field>
          <Field label="Descripción">
            <Textarea value={form.description} onChange={upd('description')} rows={3} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Inicio planificado">
              <Input type="datetime-local" value={form.planned_start} onChange={upd('planned_start')} />
            </Field>
            <Field label="Fin planificado">
              <Input type="datetime-local" value={form.planned_end} onChange={upd('planned_end')} />
            </Field>
          </div>
        </div>

        <div className="card" style={{ padding: 24, marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>Plan</h3>
          <Field label="Plan de implementación">
            <Textarea value={form.implementation_plan} onChange={upd('implementation_plan')} rows={4} />
          </Field>
          <Field label="Plan de rollback">
            <Textarea value={form.rollback_plan} onChange={upd('rollback_plan')} rows={3} />
          </Field>
          <Field label="Plan de pruebas">
            <Textarea value={form.test_plan} onChange={upd('test_plan')} rows={3} />
          </Field>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={() => navigate('/changes')}
            style={{ padding: '9px 18px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
            Cancelar
          </button>
          <button type="submit" disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: saving ? 'var(--ink-2)' : 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? <><RefreshCw size={13} style={{ animation: 'spin .8s linear infinite' }} /> Guardando...</> : <><Save size={13} /> Crear RFC</>}
          </button>
        </div>
      </form>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Detalle ──────────────────────────────────────────────────────────────────
export default function ChangeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [change, setChange]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [tab, setTab]             = useState('Detalles');
  const [editMode, setEditMode]   = useState(false);
  const [form, setForm]           = useState({});
  const [statusNote, setStatusNote] = useState('');
  const [error, setError]         = useState('');
  const [generating, setGenerating]   = useState(false);
  const [aiSuccess, setAiSuccess]     = useState(false);
  const [creatingKb, setCreatingKb]   = useState(false);
  const [kbSuccess, setKbSuccess]     = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/changes/${id}`, { headers });
      if (r.ok) {
        const data = await r.json();
        setChange(data);
        setForm({
          title: data.title, description: data.description || '',
          priority: data.priority, risk_level: data.risk_level, impact: data.impact,
          implementation_plan: data.implementation_plan || '',
          rollback_plan: data.rollback_plan || '',
          test_plan: data.test_plan || '',
          planned_start: data.planned_start ? data.planned_start.slice(0, 16) : '',
          planned_end:   data.planned_end   ? data.planned_end.slice(0, 16)   : '',
          approval_notes: data.approval_notes || '',
          closure_notes:  data.closure_notes  || '',
        });
      }
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  async function handleSave() {
    setSaving(true); setError('');
    try {
      const r = await fetch(`${API_URL}/changes/${id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) { const d = await r.json(); setError(d.error || 'Error guardando.'); return; }
      setChange(await r.json());
      setEditMode(false);
    } catch { setError('Error de conexión.'); }
    finally { setSaving(false); }
  }

  async function handleCreateKb() {
    setCreatingKb(true); setError(''); setKbSuccess(null);
    try {
      const r = await fetch(`${API_URL}/changes/${id}/create-kb`, {
        method: 'POST', headers,
      });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Error creando artículo KB.'); return; }
      setKbSuccess(data.article?.id);
    } catch { setError('Error de conexión.'); }
    finally { setCreatingKb(false); }
  }

  async function handleGeneratePlan() {
    setGenerating(true); setError(''); setAiSuccess(false);
    try {
      const r = await fetch(`${API_URL}/changes/${id}/generate-plan`, {
        method: 'POST',
        headers,
      });
      if (!r.ok) { const d = await r.json(); setError(d.error || 'Error generando plan.'); return; }
      const plans = await r.json();
      setForm(f => ({
        ...f,
        implementation_plan: plans.implementation_plan || f.implementation_plan,
        rollback_plan:        plans.rollback_plan        || f.rollback_plan,
        test_plan:            plans.test_plan            || f.test_plan,
      }));
      setEditMode(true);
      setAiSuccess(true);
      setTimeout(() => setAiSuccess(false), 4000);
    } catch { setError('Error de conexión.'); }
    finally { setGenerating(false); }
  }

  async function handleTransition(newStatus) {
    setSaving(true); setError('');
    try {
      const r = await fetch(`${API_URL}/changes/${id}/status`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, note: statusNote }),
      });
      if (!r.ok) { const d = await r.json(); setError(d.error || 'Error.'); return; }
      const updated = await r.json();
      setChange(updated);
      setStatusNote('');
    } catch { setError('Error de conexión.'); }
    finally { setSaving(false); }
  }

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}><RefreshCw size={20} style={{ animation: 'spin .8s linear infinite' }} /></div>;
  if (!change) return <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>Cambio no encontrado.</div>;

  const ts = STATUS_STYLE[change.status] || STATUS_STYLE.draft;
  const tt = TYPE_STYLE[change.change_type] || TYPE_STYLE.normal;
  const allowed = TRANSITIONS[change.status] || [];
  const history = Array.isArray(change.status_history) ? change.status_history : [];

  const isEditable = !['closed', 'cancelled'].includes(change.status);

  return (
    <div style={{ maxWidth: 920 }}>
      {/* Back */}
      <button onClick={() => navigate('/changes')}
        style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--ink-2)', fontSize: 13, cursor: 'pointer', marginBottom: 16, padding: 0 }}>
        <ChevronLeft size={14} /> Gestión de Cambios
      </button>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            {change.change_type === 'emergency' && <Zap size={16} style={{ color: 'var(--red)' }} />}
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-2)' }}>{change.change_number}</span>
            <Badge s={change.status} />
            <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, color: tt.color,}}>{tt.label}</span>
          </div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>{change.title}</h1>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
            Creado {new Date(change.created_at).toLocaleDateString('es-AR')} por {change.requested_by_name || '—'}
          </div>
          {change.source === 'patch' && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, fontWeight: 600, color: '#3730A3',}}>
              <Shield size={13} /> Origen: Gestión de Parches{change.patch_kb ? ` · ${change.patch_kb}` : ''}
              <a href="/parches" style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 700 }}>ver módulo →</a>
            </div>
          )}
        </div>
        {isEditable && (
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {editMode ? (
              <>
                <button onClick={() => setEditMode(false)}
                  style={{ padding: '8px 14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
                <button onClick={handleSave} disabled={saving}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {saving ? <RefreshCw size={12} style={{ animation: 'spin .8s linear infinite' }} /> : <Save size={12} />} Guardar
                </button>
              </>
            ) : (
              <button onClick={() => setEditMode(true)}
                style={{ padding: '8px 14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
                Editar
              </button>
            )}
          </div>
        )}
      </div>

      {error && <div style={{ background: 'var(--red-dim)', border: '1px solid var(--red-dim)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)', marginBottom: 16 }}>{error}</div>}

      {/* Acciones de estado */}
      {allowed.length > 0 && (
        <div className="card" style={{ padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginRight: 4 }}>Transición:</span>
          {allowed
            .filter(s => ['approved', 'rejected'].includes(s) ? canApprove(currentUser) : canWrite(currentUser))
            .map(s => {
              const st = TRANSITION_STYLE[s] || { bg: 'var(--border)', color: 'var(--ink-2)' };
              return (
                <button key={s} onClick={() => handleTransition(s)} disabled={saving}
                  style={{ padding: '7px 14px', background: st.bg, color: st.color, border: st.border || 'none', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {TRANSITION_LABELS[s] || s}
                </button>
              );
            })}
          <input value={statusNote} onChange={e => setStatusNote(e.target.value)} placeholder="Nota (opcional)..."
            style={{ flex: 1, minWidth: 180, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 12 }} />
        </div>
      )}

      {/* Botón crear artículo KB — visible cuando cerrado */}
      {change.status === 'closed' && (
        <div className="card" style={{ padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <BookOpen size={14} style={{ color: '#0369A1' }} />
              Base de conocimiento
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>
              {kbSuccess ? 'Artículo KB generado.' : 'Generá un artículo KB con IA a partir de este cambio para documentar el procedimiento.'}
            </div>
          </div>
          {kbSuccess ? (
            <button onClick={() => navigate('/knowledge-base')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#0369A1', color: 'var(--surface)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
              <BookOpen size={13} /> Ver artículo KB
            </button>
          ) : (
            <button onClick={handleCreateKb} disabled={creatingKb}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: creatingKb ? '#E0F2FE' : '#0369A1', color: creatingKb ? '#0369A1' : '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: creatingKb ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {creatingKb ? <><RefreshCw size={13} style={{ animation: 'spin .8s linear infinite' }} /> Generando...</> : <><Sparkles size={13} /> Crear artículo KB</>}
            </button>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ borderBottom: '2px solid var(--border)', marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: '8px 16px', border: 'none', background: 'none', fontSize: 13, fontWeight: tab === t ? 700 : 400, color: tab === t ? 'var(--ink)' : 'var(--ink-2)', borderBottom: tab === t ? '2px solid var(--ink)' : '2px solid transparent', marginBottom: -2, cursor: 'pointer' }}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab: Detalles */}
      {tab === 'Detalles' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><FileText size={14} /> Información general</h3>
            <Field label="Descripción">
              <Textarea value={form.description} onChange={upd('description')} disabled={!editMode} rows={4} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Prioridad">
                <Select value={form.priority} onChange={upd('priority')} disabled={!editMode}>
                  <option value="baja">Baja</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                  <option value="critica">Crítica</option>
                </Select>
              </Field>
              <Field label="Riesgo">
                <Select value={form.risk_level} onChange={upd('risk_level')} disabled={!editMode}>
                  <option value="bajo">Bajo</option>
                  <option value="medio">Medio</option>
                  <option value="alto">Alto</option>
                  <option value="critico">Crítico</option>
                </Select>
              </Field>
              <Field label="Impacto">
                <Select value={form.impact} onChange={upd('impact')} disabled={!editMode}>
                  <option value="bajo">Bajo</option>
                  <option value="medio">Medio</option>
                  <option value="alto">Alto</option>
                </Select>
              </Field>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><Calendar size={14} /> Fechas</h3>
              <Field label="Inicio planificado">
                <Input type="datetime-local" value={form.planned_start} onChange={upd('planned_start')} disabled={!editMode} />
              </Field>
              <Field label="Fin planificado">
                <Input type="datetime-local" value={form.planned_end} onChange={upd('planned_end')} disabled={!editMode} />
              </Field>
              {change.actual_start && (
                <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 8 }}>
                  Inicio real: {new Date(change.actual_start).toLocaleString('es-AR')}
                </div>
              )}
              {change.actual_end && (
                <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                  Fin real: {new Date(change.actual_end).toLocaleString('es-AR')}
                </div>
              )}
            </div>

            <div className="card" style={{ padding: 20 }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><User size={14} /> Asignación</h3>
              <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                <div style={{ marginBottom: 8 }}><span style={{ color: 'var(--ink-3)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>Asignado a</span><br />{change.assigned_to_name || '—'}</div>
                <div><span style={{ color: 'var(--ink-3)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>Solicitado por</span><br />{change.requested_by_name || '—'}</div>
              </div>
            </div>
          </div>

          {/* Notas de aprobación / cierre */}
          {(change.status === 'pending_approval' || change.approval_notes) && (
            <div className="card" style={{ padding: 20, gridColumn: '1 / -1' }}>
              <Field label="Notas de aprobación">
                <Textarea value={form.approval_notes} onChange={upd('approval_notes')} disabled={!editMode} rows={3} />
              </Field>
            </div>
          )}
          {(change.status === 'review' || change.status === 'closed' || change.closure_notes) && (
            <div className="card" style={{ padding: 20, gridColumn: '1 / -1' }}>
              <Field label="Notas de cierre / revisión">
                <Textarea value={form.closure_notes} onChange={upd('closure_notes')} disabled={!editMode} rows={3} />
              </Field>
            </div>
          )}
        </div>
      )}

      {/* Tab: Plan */}
      {tab === 'Plan' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Barra IA */}
          <div className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Sparkles size={14} style={{ color: 'var(--ink-2)' }} />
                Asistente IA
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>
                Generá borradores de los tres planes en base al contexto del RFC. Podés editarlos antes de guardar.
              </div>
            </div>
            <button onClick={handleGeneratePlan} disabled={generating}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: generating ? '#EDE9FE' : 'var(--ink-2)', color: generating ? 'var(--ink-2)' : '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: generating ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {generating
                ? <><RefreshCw size={13} style={{ animation: 'spin .8s linear infinite' }} /> Generando...</>
                : <><Sparkles size={13} /> Generar con IA</>}
            </button>
          </div>

          {aiSuccess && (
            <div style={{ background: 'var(--green-dim)', border: '1px solid var(--green-dim)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--green)' }}>
              Planes generados. Revisá el contenido y guardá cuando estés conforme.
            </div>
          )}

          {[
            { key: 'implementation_plan', label: 'Plan de implementación', icon: FileText },
            { key: 'rollback_plan',        label: 'Plan de rollback',        icon: Shield },
            { key: 'test_plan',            label: 'Plan de pruebas',         icon: AlertTriangle },
          ].map(({ key, label, icon: Icon }) => (
            <div key={key} className="card" style={{ padding: 20 }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><Icon size={14} /> {label}</h3>
              <Textarea value={form[key]} onChange={upd(key)} disabled={!editMode} rows={6} />
            </div>
          ))}
          {editMode && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={handleSave} disabled={saving}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 18px', background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <Save size={12} /> Guardar plan
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tab: Activos afectados */}
      {tab === 'Activos afectados' && (
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><HardDrive size={14} /> Activos afectados</h3>
          {!change.affected_assets?.length ? (
            <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
              No hay activos vinculados a este cambio.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {change.affected_assets.map((a, i) => (
                <span key={i} style={{ fontSize: 12, color: 'var(--ink-2)', fontWeight: 500 }}>
                  {typeof a === 'string' ? a : a.hostname || a.name || JSON.stringify(a)}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Historial */}
      {tab === 'Historial' && (
        <div className="card" style={{ padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><History size={14} /> Historial de estados</h3>
          {history.length === 0 ? (
            <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Sin historial.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {[...history].reverse().map((h, i) => {
                const toSt = STATUS_STYLE[h.to] || STATUS_STYLE.draft;
                return (
                  <div key={i} style={{ display: 'flex', gap: 14, paddingBottom: i < history.length - 1 ? 16 : 0, marginBottom: i < history.length - 1 ? 16 : 0, borderBottom: i < history.length - 1 ? '1px solid var(--bg)' : 'none' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: toSt.bg, border: `2px solid ${toSt.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Clock size={13} style={{ color: toSt.color }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                        {h.from ? `${STATUS_STYLE[h.from]?.label || h.from} → ` : ''}{toSt.label}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>
                        {h.by} · {new Date(h.at).toLocaleString('es-AR')}
                      </div>
                      {h.note && <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 4, fontStyle: 'italic' }}>"{h.note}"</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
